// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsRemoteCollections from '../../api/actionsRemoteCollections';
// Components
import ModalSendMail from '@/common-utils/components/renders/modal/components/modals/ModalSendMail';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import { DateTime } from 'luxon';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsRemoteCollection } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.type
 * @param root0.remoteCollection
 * @returns {JSX} ModalSendMail
*/
function ModalSendRemoteCollectionByMail({ isOpen, setIsOpen, remoteCollection }) {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Constants
    const MAX_EMAILS_TO_SEND = 5;

    // State
    const [renderList, setRenderList] = useState([{ email: '', isValid: true }]);

    // Toast
    const { toast } = useToast();

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.send_by_mail.success' namespace={nsRemoteCollection} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.send_by_mail.UNKNOWN_ERROR' namespace={nsRemoteCollection} />,
            variant: 'danger'
        });
    };
    const sendRemoteCollectionByMail = actionsRemoteCollections.sendRemoteCollectionByMail(onSuccess, onError);

    return (
        <ModalSendMail
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onValidate={() => {
                sendRemoteCollectionByMail.mutate({
                    remoteCollectionId: remoteCollection.uuid,
                    provider: rootOrganization.uuid,
                    emails: renderList.map(user => user.email)
                });
            }}
            isLoading={sendRemoteCollectionByMail.isLoading}
            header={{ title: 'modal.send_by_mail.title.remote_collection', namespace: nsRemoteCollection }}
            message={{
                entity: 'modal.send_by_mail.message.remote_collection', namespace: nsRemoteCollection,
                data: {
                    contract: remoteCollection.contract,
                    application: remoteCollection.application,
                    localTransactionDate: DateTime.fromISO(remoteCollection.localTransactionDateTime).toLocaleString(DateTime.DATE_SHORT),
                    localTransactionTime: DateTime.fromISO(remoteCollection.localTransactionDateTime).toLocaleString(DateTime.TIME_SIMPLE)
                }
            }}
            confirm={{ entity: `modal.send_by_mail.confirm.${renderList.length > 1 ? 'multiple' : 'single'}`, namespace: nsRemoteCollection }}
            renderList={renderList}
            setRenderList={setRenderList}
            maxEmailsNumber={MAX_EMAILS_TO_SEND}
        />
    );
}

ModalSendRemoteCollectionByMail.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    remoteCollection: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        contract: PropTypes.string,
        application: PropTypes.string,
        localTransactionDateTime: PropTypes.string
    }).isRequired
};

export default ModalSendRemoteCollectionByMail;